import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Lock, BedDouble, Bath, Maximize, ChevronRight, Shield, Crosshair, Network, Calendar, Send, Check, Compass, Hammer, Users, Mail, Ruler, Building2, Layers } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Trilingual text helper
const tx = (lang, fr, en, pt) => lang === 'fr' ? fr : lang === 'pt' ? pt : en;

// ==================== HERO SECTION (WHITE) ====================
const HeroSection = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center bg-white" data-testid="hero-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="w-10 h-px bg-[#E5B1A1]" />
            <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.35em] font-medium">
              Moon River Real Estate
            </span>
          </motion.div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1B] leading-[1.1] mb-8">
            {language === 'fr' ? (
              <>
                <span className="block">L'immobilier n'est</span>
                <span className="block">pas un pari.</span>
                <span className="block text-[#E5B1A1] mt-2">C'est une ingénierie de précision.</span>
              </>
            ) : language === 'pt' ? (
              <>
                <span className="block">O imobiliário não</span>
                <span className="block">é uma aposta.</span>
                <span className="block text-[#E5B1A1] mt-2">É engenharia de precisão.</span>
              </>
            ) : (
              <>
                <span className="block">Real estate is</span>
                <span className="block">not a gamble.</span>
                <span className="block text-[#E5B1A1] mt-2">It's precision engineering.</span>
              </>
            )}
          </h1>

          <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed mb-10 max-w-lg">
            {tx(language, "Cabinet Confidentiel d'Ingénierie & de Déploiement d'Actifs.", "Confidential Cabinet of Engineering & Asset Deployment.", "Gabinete Confidencial de Engenharia & Implementação de Ativos.")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => navigate('/#portfolio')}
              className="group px-8 py-4 text-base font-medium tracking-wide transition-all duration-300 border-0 text-white"
              style={{ backgroundColor: '#E5B1A1' }}
              data-testid="hero-cta-primary"
            >
              {tx(language, 'Accéder au Cabinet', 'Access the Cabinet', 'Aceder ao Gabinete')}
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => navigate('/#appointment')}
              variant="outline"
              className="px-8 py-4 text-base font-medium border-[#1A1A1B]/20 text-[#1A1A1B] hover:bg-[#1A1A1B]/5 hover:border-[#1A1A1B]/40 transition-all"
              data-testid="hero-cta-secondary"
            >
              {tx(language, 'Prendre rendez-vous', 'Book appointment', 'Marcar consulta')}
              <Calendar size={18} className="ml-2 text-[#E5B1A1]" />
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="relative">
            <img
              src="/capvert-terrain.jpg"
              alt="Moon River Real Estate - Cap-Vert"
              className="w-full h-[420px] lg:h-[540px] object-cover"
            />
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#E5B1A1]/20 -z-10" />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-[#6B6B6B] uppercase tracking-widest">Scroll</span>
          <div className="w-6 h-10 border border-[#E5B1A1]/40 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#E5B1A1] rounded-full mt-2"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// ==================== EXPERTISE SECTION (NOIR MAT) ====================
const ExpertiseSection = () => {
  const { language } = useLanguage();

  const pillars = [
    {
      icon: Shield,
      title: tx(language, 'Stratégie & Ingénierie', 'Strategy & Engineering', 'Estratégia & Engenharia'),
      description: tx(language, "Ingénierie juridique et fiscale, structuration de Holdings, architecture financière au Cap-Vert.", "Legal and tax engineering, Holding structuring, financial architecture in Cape Verde.", "Engenharia jurídica e fiscal, estruturação de Holdings, arquitetura financeira em Cabo Verde.")
    },
    {
      icon: Hammer,
      title: tx(language, 'Valorisation & Design', 'Enhancement & Design', 'Valorização & Design'),
      description: tx(language, "Pilotage de chantier rigoureux, maîtrise d'ouvrage, design de rendement & concepts hôteliers d'élite.", "Site monitoring, project management, elite hotel concepts.", "Gestão rigorosa de obra, direção de projeto, design de rendimento & conceitos hoteleiros de elite.")
    },
    {
      icon: Network,
      title: tx(language, 'Réseau Off-Market', 'Off-Market Network', 'Rede Off-Market'),
      description: tx(language, "Arbitrage d'opportunités exclusives, carnet d'adresses stratégique, écosystème de partenaires locaux.", "Opportunity arbitration, strategic address book, partner ecosystem.", "Arbitragem de oportunidades exclusivas, agenda estratégica de contactos, ecossistema de parceiros locais.")
    }
  ];

  return (
    <section
      id="about"
      className="relative py-24 lg:py-32 overflow-hidden bg-[#1A1A1B]"
      data-testid="about-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Split: Text + Image */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          {/* Left - Editorial text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-[#E5B1A1]" />
              <Crosshair size={20} className="text-[#E5B1A1]" strokeWidth={1} />
              <div className="w-12 h-px bg-[#E5B1A1]" />
            </div>

            <h2 className="font-serif text-4xl md:text-5xl text-[#E5B1A1] mb-6">
              {tx(language, "L'Ingénierie au service de l'Actif", 'Engineering in Service of Assets', "A Engenharia ao Serviço do Ativo")}
            </h2>

            <p className="text-white/90 text-base md:text-lg leading-[1.9] tracking-wide mb-8">
              {tx(language, "Votre patrimoine mérite l'excellence d'un cabinet privé de gestion d'actifs. J'aborde chacun de vos projets comme une mission de haute ingénierie. Forte de 15 ans d'expertise entre la France et le Cap-Vert, je ne me contente pas de vous conseiller : je pilote votre stratégie d'acquisition jusqu'à votre valorisation patrimoniale, sécurise l'intégralité de vos démarches et transforme vos ambitions en performances tangibles.", "Your assets deserve the excellence of a private asset management firm. I approach each of your projects as a high-engineering mission. With 15 years of expertise between France and Cape Verde, I don't just advise you: I drive your acquisition strategy through to your asset valuation, secure all your processes and transform your ambitions into tangible performance.", "O seu património merece a excelência de um gabinete privado de gestão de ativos. Abordo cada um dos seus projetos como uma missão de alta engenharia. Com 15 anos de experiência entre França e Cabo Verde, não me limito a aconselhar: conduzo a sua estratégia de aquisição até à valorização patrimonial, asseguro todas as suas diligências e transformo as suas ambições em resultados tangíveis.")}
            </p>

            <p className="font-serif text-xl md:text-2xl text-[#E5B1A1] leading-relaxed mb-8 italic">
              {tx(language, "« Vous bâtissez votre liberté financière. Je garantis vos fondations. »", "You build your financial freedom. I guarantee your foundations.", "Você constrói a sua liberdade financeira. Eu garanto as suas fundações.")}
            </p>

          </motion.div>

          {/* Right - Moon River Building Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm">
              <img
                src="/moonriver-building.png"
                alt="Moon River Real Estate"
                className="w-full h-[480px] lg:h-[560px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-[#1A1A1B] to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-px bg-[#E5B1A1]/60" />
                  <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.3em] font-medium">
                    {tx(language, 'Vision & Exécution', 'Vision & Execution', 'Visão & Execução')}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pillars */}
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="relative p-8 bg-white/[0.03] border border-[#E5B1A1]/10 hover:border-[#E5B1A1]/30 transition-all group"
            >
              <div className="w-14 h-14 border border-[#E5B1A1]/30 flex items-center justify-center mb-6 group-hover:bg-[#E5B1A1]/10 transition-colors">
                <pillar.icon size={24} strokeWidth={1} className="text-[#E5B1A1]" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-4">{pillar.title}</h3>
              <p className="text-white/60 leading-relaxed">{pillar.description}</p>
              <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#E5B1A1]/30 to-transparent" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== SOCIAL PROOF / STATS (WHITE) ====================
const StatsSection = () => {
  const { language } = useLanguage();

  const stats = [
    { value: '15+', label: tx(language, "Ans d'Expertise", 'Years of Expertise', "Anos de Experiência"),
      subtitle: tx(language, "Une solide expérience métier forgée entre la France et le Cap-Vert.", "A solid professional experience forged between France and Cape Verde.", "Uma sólida experiência profissional forjada entre França e Cabo Verde.") },
    { value: '250', prefix: '', suffix: 'M€', label: tx(language, 'Volume Transigé', 'Transaction Volume', 'Volume Transacionado'),
      subtitle: tx(language, "Volume global incluant les transactions réalisées, les projets de construction supervisés et les actifs valorisés.", "Total volume including completed transactions, supervised construction projects and enhanced assets.", "Volume global incluindo as transações realizadas, os projetos de construção supervisionados e os ativos valorizados.") },
    { value: '120+', label: tx(language, 'Investisseurs Accompagnés', 'Investors Supported', 'Investidores Acompanhados'),
      subtitle: tx(language, "Des profils internationaux et institutionnels guidés avec rigueur dans le déploiement de leurs capitaux.", "International and institutional profiles rigorously guided in deploying their capital.", "Perfis internacionais e institucionais orientados com rigor na implementação dos seus capitais.") },
    { value: '45+', label: tx(language, 'Actifs sous Gestion', 'Assets under Management', 'Ativos sob Gestão'),
      subtitle: tx(language, "Patrimoines immobiliers pilotés et valorisés au quotidien pour garantir une performance locative maximale.", "Real estate portfolios managed and enhanced daily to ensure maximum rental performance.", "Patrimónios imobiliários geridos e valorizados diariamente para garantir um desempenho locativo máximo.") },
  ];

  return (
    <section className="relative py-24 lg:py-32 bg-white" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-[#E5B1A1]" />
            <Users size={20} className="text-[#E5B1A1]" strokeWidth={1} />
            <div className="w-12 h-px bg-[#E5B1A1]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1B] mb-4">
            {tx(language, 'Nos Chiffres', 'Our Numbers', 'Os Nossos Números')}
          </h2>
          <p className="text-[#6B6B6B] text-base md:text-lg max-w-xl mx-auto">
            {tx(language, "Des résultats concrets au service d'investisseurs exigeants.", "Concrete results serving demanding investors.", "Des résultats concrets au service d'investisseurs exigeants.")}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative p-8 text-center group border border-[#1A1A1B]/10 hover:border-[#E5B1A1]/50 transition-all bg-white"
            >
              <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-[#E5B1A1]/40" />
              <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-[#E5B1A1]/40" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-[#E5B1A1]/40" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-[#E5B1A1]/40" />
              <span className="font-serif text-4xl md:text-5xl text-[#E5B1A1] block mb-1">
                {stat.prefix || ''}{stat.value}{stat.suffix || ''}
              </span>
              <span className="text-[#1A1A1B] text-sm font-semibold uppercase tracking-wide block mb-3">{stat.label}</span>
              <span className="text-[#6B6B6B] text-xs leading-relaxed block">{stat.subtitle}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== SERVICES VISUAL (NOIR MAT) ====================
const ServicesVisualSection = () => (
  <section className="bg-[#1A1A1B] py-0">
    <div className="max-w-7xl mx-auto">
      <img
        src="/moonriver-services.png"
        alt="Moon River Real Estate Services"
        className="w-full h-auto object-cover"
        data-testid="services-visual"
      />
    </div>
  </section>
);

// ==================== TESTIMONIALS SECTION (NOIR MAT) ====================
const TestimonialsSection = () => {
  const { language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      quote_fr: "Moon River a identifié pour moi une villa off-market à Sal avant même qu'elle n'arrive sur le marché. En 6 mois, mon rendement locatif dépassait déjà les 9%. C'est de l'ingénierie, pas de l'immobilier classique.",
      quote_en: "Moon River identified an off-market villa in Sal for me before it even hit the market. Within 6 months, my rental yield was already exceeding 9%. This is engineering, not standard real estate.",
      name: "Laurent M.",
      role_fr: "Investisseur privé — Paris",
      role_en: "Private investor — Paris",
      investment: "245 000 €"
    },
    {
      quote_fr: "La structuration en holding que l'équipe m'a proposée m'a fait économiser 35% sur la fiscalité de mes 3 biens à Boa Vista. Leur approche est chirurgicale.",
      quote_en: "The holding structure the team proposed saved me 35% on taxes across my 3 properties in Boa Vista. Their approach is surgical.",
      quote_pt: "A estruturação em holding que a equipa me propôs permitiu-me poupar 35% na fiscalidade dos meus 3 imóveis em Boa Vista. A abordagem deles é cirúrgica.",
      name: "Sophie D.",
      role_fr: "Directrice financière — Lyon",
      role_en: "CFO — Lyon",
      role_pt: "Diretora financeira — Lyon",
      investment: "580 000 €"
    },
    {
      quote_fr: "J'ai travaillé avec 4 agences avant de trouver Moon River. La différence ? Ils m'ont dit non sur 2 projets avant de me présenter le bon. Ce niveau de conseil n'existe nulle part ailleurs.",
      quote_en: "I worked with 4 agencies before finding Moon River. The difference? They said no to 2 projects before presenting the right one. This level of advisory doesn't exist anywhere else.",
      quote_pt: "Trabalhei com 4 agências antes de encontrar a Moon River. A diferença? Disseram-me não a 2 projetos antes de me apresentarem o certo. Este nível de consultoria não existe em mais lado nenhum.",
      name: "Carlos A.",
      role_fr: "Entrepreneur — Lisbonne",
      role_en: "Entrepreneur — Lisbon",
      role_pt: "Empresário — Lisboa",
      investment: "320 000 €"
    },
    {
      quote_fr: "Je cherchais à diversifier hors de l'Europe continentale sans prendre de risques inconsidérés. Moon River m'a présenté un dossier béton à Santiago avec bail commercial déjà signé. Rentabilité nette de 7,5% dès la première année. Du sérieux, du concret.",
      quote_en: "I was looking to diversify outside continental Europe without taking reckless risks. Moon River presented me with a rock-solid file in Santiago with a commercial lease already signed. Net yield of 7.5% from the first year. Serious and concrete.",
      quote_pt: "Procurava diversificar fora da Europa continental sem correr riscos imprudentes. A Moon River apresentou-me um dossier sólido em Santiago com contrato comercial já assinado. Rentabilidade líquida de 7,5% logo no primeiro ano. Sério e concreto.",
      name: "Marc-Antoine W.",
      role_fr: "Gérant de patrimoine — Genève",
      role_en: "Wealth Manager — Geneva",
      role_pt: "Gestor de património — Genebra",
      investment: "410 000 €"
    },
    {
      quote_fr: "Ce qui m'a convaincu, c'est leur transparence totale. Pas de promesses irréalistes, juste une analyse froide du marché capverdien et un accompagnement jusqu'à la remise des clés. Mon T3 à Mindelo génère aujourd'hui un cash-flow positif chaque mois.",
      quote_en: "What convinced me was their total transparency. No unrealistic promises, just a cold analysis of the Cape Verdean market and support right through to key handover. My 3-bed in Mindelo now generates positive cash flow every month.",
      quote_pt: "O que me convenceu foi a transparência total. Sem promessas irrealistas, apenas uma análise fria do mercado cabo-verdiano e acompanhamento até à entrega das chaves. O meu T3 em Mindelo gera hoje cash-flow positivo todos os meses.",
      name: "James H.",
      role_fr: "Directeur d'investissement — Londres",
      role_en: "Investment Director — London",
      role_pt: "Diretor de investimento — Londres",
      investment: "295 000 €"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section className="relative py-24 lg:py-32 bg-[#1A1A1B] overflow-hidden" data-testid="testimonials-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1]/20 to-transparent" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-[#E5B1A1]" />
            <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.4em] font-medium">
              {tx(language, 'Témoignages', 'Testimonials', 'Testemunhos')}
            </span>
            <div className="w-12 h-px bg-[#E5B1A1]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-white">
            {tx(language, 'La Parole aux Investisseurs', 'Investors Speak', 'A Palavra aos Investidores')}
          </h2>
        </motion.div>

        <div className="relative min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="w-full"
            >
              <div className="text-center">
                <div className="text-[#E5B1A1]/30 text-8xl font-serif leading-none mb-4 select-none">"</div>
                <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl text-white leading-relaxed mb-10 max-w-3xl mx-auto italic">
                  {language === 'pt' ? (testimonials[activeIndex].quote_pt || testimonials[activeIndex].quote_en) : language === 'fr' ? testimonials[activeIndex].quote_fr : testimonials[activeIndex].quote_en}
                </blockquote>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-px bg-[#E5B1A1]/50 mb-2" />
                  <span className="text-[#E5B1A1] font-medium text-lg tracking-wide">{testimonials[activeIndex].name}</span>
                  <span className="text-white/60 text-sm">
                    {language === 'pt' ? (testimonials[activeIndex].role_pt || testimonials[activeIndex].role_en) : language === 'fr' ? testimonials[activeIndex].role_fr : testimonials[activeIndex].role_en}
                  </span>
                  <span className="text-[#E5B1A1]/60 text-xs uppercase tracking-[0.3em] mt-1">
                    {tx(language, 'Investissement', 'Investment', 'Investimento')}: {testimonials[activeIndex].investment}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex justify-center gap-3 mt-10">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`transition-all duration-300 ${
                activeIndex === index
                  ? 'w-10 h-1.5 bg-[#E5B1A1]'
                  : 'w-4 h-1.5 bg-[#E5B1A1]/20 hover:bg-[#E5B1A1]/40'
              }`}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1]/20 to-transparent" />
    </section>
  );
};

// ==================== PORTFOLIO SECTION (CABINET CONFIDENTIEL - WHITE) ====================
const PortfolioSection = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('properties');

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${API}/api/properties`, { withCredentials: true });
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const filteredProperties = properties.filter(p =>
    activeTab === 'properties' ? p.property_type !== 'land' : p.property_type === 'land'
  );

  const tabs = [
    { id: 'properties', label: tx(language, 'Propriétés & Villas', 'Properties & Villas', 'Propriedades & Vilas'), icon: Building2 },
    { id: 'land', label: tx(language, 'Opportunités Foncières', 'Land Opportunities', 'Oportunidades Fundiárias'), icon: Layers },
  ];

  return (
    <section
      id="portfolio"
      className="relative py-24 lg:py-32 bg-white"
      data-testid="portfolio-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-px bg-[#E5B1A1]/50" />
              <Shield size={18} className="text-[#E5B1A1]" strokeWidth={1} />
            </div>
            <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.4em] font-medium px-4 py-2 border border-[#E5B1A1]/30">
              {tx(language, 'Cabinet Confidentiel', 'Private Cabinet', 'Gabinete Confidencial')}
            </span>
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-[#E5B1A1]" strokeWidth={1} />
              <div className="w-8 h-px bg-[#E5B1A1]/50" />
            </div>
          </div>

          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1B] mb-4">
            {tx(language, 'Vos Actifs Sous Haute Protection', 'Your Assets Under High Protection', 'Os Seus Ativos Sob Alta Proteção')}
          </h2>
          <p className="text-[#6B6B6B] text-base md:text-lg max-w-2xl mx-auto">
            {tx(language, "Accédez à votre espace sécurisé pour consulter vos investissements et découvrir nos opportunités Off-Market exclusives.", "Access your secured space to review your investments and discover our exclusive Off-Market opportunities.", "Aceda ao seu espaço seguro para consultar os seus investimentos e descobrir as nossas oportunidades Off-Market exclusivas.")}
          </p>
        </motion.div>

        {/* TABS */}
        <div className="flex items-center justify-center gap-4 mb-16" data-testid="portfolio-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2.5 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-all border ${
                activeTab === tab.id
                  ? 'bg-[#1A1A1B] text-[#E5B1A1] border-[#1A1A1B]'
                  : 'bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-[#E5B1A1]/50 hover:text-[#1A1A1B]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#E5B1A1]/30 border-t-[#E5B1A1] rounded-full animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.35 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {filteredProperties.map((property, index) => (
                <motion.div
                  key={property.property_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative bg-white border border-[#E5B1A1]/20 hover:border-[#E5B1A1]/50 transition-all overflow-hidden shadow-sm hover:shadow-md"
                  data-testid={`property-card-${property.property_id}`}
                >
                  <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-[#E5B1A1]/30 z-10" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-[#E5B1A1]/30 z-10" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-[#E5B1A1]/30 z-10" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-[#E5B1A1]/30 z-10" />

                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={property.images?.[0] || 'https://images.unsplash.com/photo-1639663742190-1b3dba2eebcf'}
                      alt={language === 'pt' ? (property.title_pt || property.title_en) : language === 'fr' ? property.title_fr : property.title_en}
                      className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                        property.is_off_market && !user ? 'blur-sm grayscale' : ''
                      }`}
                    />

                    {property.is_off_market && !user && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
                        <div className="relative">
                          <div className="w-20 h-20 border-2 border-[#E5B1A1]/30 flex items-center justify-center">
                            <div className="w-14 h-14 border border-[#E5B1A1]/50 flex items-center justify-center bg-white/50">
                              <Lock size={24} className="text-[#E5B1A1]" />
                            </div>
                          </div>
                        </div>
                        <span className="text-[#1A1A1B] font-medium tracking-wide text-sm">
                          {tx(language, 'Dossier Confidentiel', 'Confidential File', 'Dossier Confidencial')}
                        </span>
                        <Button
                          onClick={() => navigate('/login')}
                          className="px-6 py-2 text-sm font-medium border-0 text-white"
                          style={{ backgroundColor: '#E5B1A1' }}
                          data-testid="portfolio-login-btn"
                        >
                          <Lock size={14} className="mr-2" />
                          {tx(language, 'Entrée Sécurisée', 'Secure Entry', 'Entrada Segura')}
                        </Button>
                      </div>
                    )}

                    <div className="absolute top-4 right-4 px-4 py-2 bg-[#1A1A1B]/90 border border-[#E5B1A1]/30">
                      <span className="text-[#E5B1A1] font-semibold">{formatPrice(property.price)}</span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-serif text-xl text-[#1A1A1B] mb-2">
                      {language === 'pt' ? (property.title_pt || property.title_en) : language === 'fr' ? property.title_fr : property.title_en}
                    </h3>
                    <p className="text-[#6B6B6B] text-sm mb-4 flex items-center gap-2">
                      <Compass size={14} className="text-[#E5B1A1]" />
                      {property.location}
                    </p>

                    {/* Indicators — adapted per type */}
                    <div className="flex gap-6 text-sm text-[#6B6B6B] mb-4">
                      {property.property_type === 'land' ? (
                        <>
                          <span className="flex items-center gap-2">
                            <Ruler size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {property.surface} m²
                          </span>
                          <span className="flex items-center gap-2">
                            <Layers size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {language === 'pt' ? (property.constructibility_pt || property.constructibility_en) : language === 'fr' ? property.constructibility_fr : property.constructibility_en}
                          </span>
                          <span className="flex items-center gap-2">
                            <Building2 size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {language === 'pt' ? (property.gabarit_pt || property.gabarit_en) : language === 'fr' ? property.gabarit_fr : property.gabarit_en}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="flex items-center gap-2">
                            <BedDouble size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {property.bedrooms}
                          </span>
                          <span className="flex items-center gap-2">
                            <Bath size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {property.bathrooms}
                          </span>
                          <span className="flex items-center gap-2">
                            <Maximize size={16} strokeWidth={1.5} className="text-[#E5B1A1]" />
                            {property.surface} m²
                          </span>
                        </>
                      )}
                    </div>

                    {user ? (
                      <Link
                        to={`/property/${property.property_id}`}
                        className="flex items-center gap-2 text-[#E5B1A1] hover:text-[#D49B8B] transition-colors text-sm font-medium"
                        data-testid={`view-details-${property.property_id}`}
                      >
                        {tx(language, 'Voir le dossier complet', 'View full file', 'Ver o dossier completo')}
                        <ChevronRight size={16} />
                      </Link>
                    ) : (
                      <button
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#E5B1A1] transition-colors text-sm"
                        data-testid={`login-to-view-${property.property_id}`}
                      >
                        <Lock size={14} />
                        {tx(language, 'Connexion requise', 'Login required', 'Sessão necessária')}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </section>
  );
};

// ==================== CONTACT SECTION (WHITE) ====================
const ContactSection = () => {
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [contactData, setContactData] = useState({
    name: '', email: '', phone: '', budget: '', investment_type: '', horizon: '', message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 2 — Appointment state
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const response = await axios.get(`${API}/api/appointments/available`);
        setAvailableSlots(response.data);
      } catch (error) { console.error('Error fetching slots:', error); }
    };
    fetchSlots();
  }, []);

  const getSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    const dayData = availableSlots.find((d) => d.date === dateStr);
    return dayData?.slots || [];
  };

  // Step 1 — Submit qualification
  const handleQualification = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/leads`, { ...contactData, language });
      toast.success(tx(language, 'Demande transmise — choisissez votre créneau', 'Request sent — choose your slot', 'Pedido transmitido — escolha o seu horário'));
      setStep(2);
    } catch (error) {
      toast.error(tx(language, 'Une erreur est survenue', 'An error occurred', 'Ocorreu um erro'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2 — Book appointment (name/email passed from step 1)
  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) { toast.error(tx(language, 'Sélectionnez une date et un créneau', 'Select a date and time slot', 'Selecione uma data e um horário')); return; }
    setIsBooking(true);
    try {
      await axios.post(`${API}/api/appointments`, {
        date: selectedDate.toISOString().split('T')[0],
        time_slot: selectedSlot,
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        subject: contactData.investment_type || 'investment',
        notes: contactData.message || '',
        language,
      });
      setIsComplete(true);
      toast.success(tx(language, 'Rendez-vous confirmé', 'Appointment confirmed', 'Rendez-vous confirmé'));
    } catch (error) { toast.error(error.response?.data?.detail || (tx(language, 'Erreur', 'Error', 'Erreur'))); } finally { setIsBooking(false); }
  };

  const inputClasses = "w-full py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1] placeholder:text-[#A3A3A0]";

  // ===== FINAL CONFIRMATION =====
  if (isComplete) {
    return (
      <section id="contact" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="p-12 border border-[#E5B1A1]/30 bg-white">
            <div className="w-16 h-16 border-2 border-[#E5B1A1] flex items-center justify-center mx-auto mb-6">
              <Check size={32} className="text-[#E5B1A1]" />
            </div>
            <h3 className="font-serif text-3xl text-[#1A1A1B] mb-4">
              {tx(language, 'Rendez-vous Confirmé', 'Appointment Confirmed', 'Consulta Confirmada')}
            </h3>
            <p className="text-[#6B6B6B]">
              {tx(language, "Votre dossier est enregistré et votre créneau réservé. Un email de confirmation vous a été envoyé.", "Your file is registered and your slot booked. A confirmation email has been sent.", "O seu dossier está registado e o seu horário reservado. Um email de confirmação foi-lhe enviado.")}
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* STEP INDICATOR */}
        <div className="flex items-center justify-center gap-3 mb-16" data-testid="step-indicator">
          <div className={`flex items-center gap-2 px-4 py-2 border transition-all ${step === 1 ? 'border-[#E5B1A1] bg-[#E5B1A1]/5' : 'border-[#E5B1A1]/30'}`}>
            <span className={`w-6 h-6 flex items-center justify-center text-xs font-semibold ${step === 1 ? 'bg-[#E5B1A1] text-white' : 'bg-[#E5B1A1] text-white'}`}>1</span>
            <span className={`text-xs uppercase tracking-wider ${step === 1 ? 'text-[#1A1A1B]' : 'text-[#6B6B6B]'}`}>
              {tx(language, 'Qualification', 'Qualification', 'Qualificação')}
            </span>
          </div>
          <div className="w-8 h-px bg-[#E5B1A1]/30" />
          <div className={`flex items-center gap-2 px-4 py-2 border transition-all ${step === 2 ? 'border-[#E5B1A1] bg-[#E5B1A1]/5' : 'border-[#E5E5E5]'}`}>
            <span className={`w-6 h-6 flex items-center justify-center text-xs font-semibold ${step === 2 ? 'bg-[#E5B1A1] text-white' : 'bg-[#E5E5E5] text-[#6B6B6B]'}`}>2</span>
            <span className={`text-xs uppercase tracking-wider ${step === 2 ? 'text-[#1A1A1B]' : 'text-[#6B6B6B]'}`}>
              {tx(language, 'Réservation', 'Booking', 'Reserva')}
            </span>
          </div>
        </div>

        {/* ===== STEP 1: QUALIFICATION ===== */}
        {step === 1 && (
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-px bg-[#E5B1A1]" />
                <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.3em]">Qualification</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1B] mb-6">
                {tx(language, 'Appel Stratégique', 'Strategic Call', 'Chamada Estratégica')}
              </h2>
              <p className="text-[#6B6B6B] text-base md:text-lg mb-8 leading-relaxed">
                {tx(language, "Chaque demande est traitée avec la plus stricte confidentialité. Votre stratégie patrimoniale mérite discrétion et excellence. J'analyse personnellement chaque dossier afin de limiter le nombre de projets suivis simultanément et vous garantir une attention totale.", "Every request is handled with the strictest confidentiality. Your asset strategy deserves discretion and excellence. I personally analyze each file to limit the number of projects followed simultaneously and guarantee you my full attention.", "Cada pedido é tratado com a mais estrita confidencialidade. A sua estratégia patrimonial merece discrição e excelência. Analiso pessoalmente cada dossier para limitar o número de projetos acompanhados simultaneamente e garantir-lhe atenção total.")}
              </p>
              <div className="space-y-4">
                {[
                  tx(language, 'Réponse sous 48h', 'Response within 48h', 'Resposta em 48h'),
                  tx(language, 'Analyse personnalisée', 'Personalized analysis', 'Análise personalizada'),
                  tx(language, 'Confidentialité garantie', 'Guaranteed confidentiality', 'Confidencialidade garantida'),
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-[#6B6B6B]">
                    <div className="w-2 h-2 bg-[#E5B1A1]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <form onSubmit={handleQualification} className="p-8 bg-white border border-[#E5E5E5] shadow-sm space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, 'Nom complet', 'Full name', 'Nome completo')}</label>
                    <Input type="text" required value={contactData.name} onChange={(e) => setContactData({ ...contactData, name: e.target.value })} className={inputClasses} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">Email</label>
                    <Input type="email" required value={contactData.email} onChange={(e) => setContactData({ ...contactData, email: e.target.value })} className={inputClasses} data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, 'Téléphone', 'Phone', 'Telefone')}</label>
                  <Input type="tel" value={contactData.phone} onChange={(e) => setContactData({ ...contactData, phone: e.target.value })} className={inputClasses} data-testid="contact-phone" />
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, "Budget d'investissement", 'Investment budget', "Orçamento de investimento")}</label>
                  <Select value={contactData.budget} onValueChange={(value) => setContactData({ ...contactData, budget: value })}>
                    <SelectTrigger className="w-full py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B]" data-testid="contact-budget"><SelectValue placeholder={tx(language, 'Sélectionner', 'Select', 'Selecionar')} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E5E5]">
                      <SelectItem value="under500" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, '< 500 000 €', '< €500,000', '< 500 000 €')}</SelectItem>
                      <SelectItem value="500to1m" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">500K - 1M €</SelectItem>
                      <SelectItem value="1mto3m" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">1M - 3M €</SelectItem>
                      <SelectItem value="3mto5m" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">3M - 5M €</SelectItem>
                      <SelectItem value="over5m" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, '> 5 000 000 €', '> €5,000,000', '> 5 000 000 €')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, "Type d'investissement", 'Investment type', "Tipo de investimento")}</label>
                  <Select value={contactData.investment_type} onValueChange={(value) => setContactData({ ...contactData, investment_type: value })}>
                    <SelectTrigger className="w-full py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B]" data-testid="contact-investment-type"><SelectValue placeholder={tx(language, 'Sélectionner', 'Select', 'Selecionar')} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E5E5]">
                      <SelectItem value="residential" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Résidentiel de prestige', 'Premium residential', 'Residencial de prestígio')}</SelectItem>
                      <SelectItem value="commercial" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Commercial haut de gamme', 'High-end commercial', 'Comercial de gama alta')}</SelectItem>
                      <SelectItem value="mixed" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Mixte', 'Mixed-use', 'Misto')}</SelectItem>
                      <SelectItem value="land" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Foncier', 'Land', 'Fundiário')}</SelectItem>
                      <SelectItem value="development" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Promotion immobilière', 'Real estate development', 'Promoção imobiliária')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, 'Horizon de placement', 'Investment horizon', 'Horizonte de investimento')}</label>
                  <Select value={contactData.horizon} onValueChange={(value) => setContactData({ ...contactData, horizon: value })}>
                    <SelectTrigger className="w-full py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B]" data-testid="contact-horizon"><SelectValue placeholder={tx(language, 'Sélectionner', 'Select', 'Selecionar')} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E5E5]">
                      <SelectItem value="short" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Court terme (< 2 ans)', 'Short term (< 2 years)', 'Curto prazo (< 2 anos)')}</SelectItem>
                      <SelectItem value="medium" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Moyen terme (2-5 ans)', 'Medium term (2-5 years)', 'Médio prazo (2-5 anos)')}</SelectItem>
                      <SelectItem value="long" className="text-[#1A1A1B] focus:bg-[#F5F5F5]">{tx(language, 'Long terme (> 5 ans)', 'Long term (> 5 years)', 'Longo prazo (> 5 anos)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] block mb-3">{tx(language, 'Message (optionnel)', 'Message (optional)', 'Mensagem (opcional)')}</label>
                  <Textarea value={contactData.message} onChange={(e) => setContactData({ ...contactData, message: e.target.value })} className="w-full min-h-[100px] bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1]" data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full py-4 text-base font-medium border-0 text-white" style={{ backgroundColor: '#E5B1A1' }} data-testid="contact-submit">
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      {tx(language, 'Valider et choisir un créneau', 'Validate and choose a slot', 'Validar e escolher um horário')}
                      <ChevronRight size={18} />
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        )}

        {/* ===== STEP 2: BOOKING (Name/Email pre-filled from step 1) ===== */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* User info recap */}
            <div className="max-w-2xl mx-auto mb-12 p-6 border border-[#E5B1A1]/20 bg-[#E5B1A1]/5" data-testid="booking-recap">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#E5B1A1] flex items-center justify-center">
                    <Check size={18} className="text-white" />
                  </div>
                  <div>
                    <span className="block text-[#1A1A1B] font-medium">{contactData.name}</span>
                    <span className="block text-[#6B6B6B] text-sm">{contactData.email}</span>
                  </div>
                </div>
                <button onClick={() => setStep(1)} className="text-[#E5B1A1] text-xs uppercase tracking-wider hover:underline" data-testid="edit-info">
                  {tx(language, 'Modifier', 'Edit', 'Modificar')}
                </button>
              </div>
            </div>

            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1B] mb-4">
                {tx(language, 'Choisissez votre Créneau', 'Choose Your Slot', 'Escolha o Seu Horário')}
              </h2>
              <p className="text-[#6B6B6B] text-base">
                {tx(language, 'Sélectionnez la date et l\'horaire qui vous conviennent.', 'Select the date and time that suit you.', 'Selecione a data e o horário que lhe convêm.')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {/* Calendar */}
              <div className="p-6 bg-white border border-[#E5E5E5] shadow-sm">
                <h3 className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] mb-6">{tx(language, 'Sélectionnez une date', 'Select a date', 'Selecione uma data')}</h3>
                <div className="calendar-light">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => { setSelectedDate(date); setSelectedSlot(''); }}
                    disabled={(date) => {
                      const dateStr = date.toISOString().split('T')[0];
                      const today = new Date().toISOString().split('T')[0];
                      return dateStr < today || !availableSlots.some((d) => d.date === dateStr);
                    }}
                    className="rounded-none border-0"
                  />
                </div>
                {selectedDate && getSlotsForDate(selectedDate).length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] mb-4">{tx(language, 'Horaires disponibles', 'Available times', 'Horários disponíveis')}</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {getSlotsForDate(selectedDate).map((slot) => (
                        <button key={slot} onClick={() => setSelectedSlot(slot)}
                          className={`p-3 text-sm transition-all border ${selectedSlot === slot ? 'bg-[#E5B1A1] text-white border-[#E5B1A1]' : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] hover:border-[#E5B1A1]'}`}
                          data-testid={`time-slot-${slot}`}
                        >{slot}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm */}
              <div className="p-8 bg-white border border-[#E5E5E5] shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] mb-6">{tx(language, 'Récapitulatif', 'Summary', 'Resumo')}</h3>
                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#6B6B6B] text-sm">{tx(language, 'Nom', 'Name', 'Nome')}</span>
                      <span className="text-[#1A1A1B] text-sm font-medium">{contactData.name}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#6B6B6B] text-sm">Email</span>
                      <span className="text-[#1A1A1B] text-sm font-medium">{contactData.email}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#6B6B6B] text-sm">{tx(language, 'Date', 'Date', 'Data')}</span>
                      <span className="text-[#1A1A1B] text-sm font-medium">{selectedDate ? selectedDate.toLocaleDateString(tx(language, 'fr-FR', 'en-GB', 'fr-FR'), { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#6B6B6B] text-sm">{tx(language, 'Horaire', 'Time', 'Horário')}</span>
                      <span className="text-[#1A1A1B] text-sm font-medium">{selectedSlot || '—'}</span>
                    </div>
                  </div>
                </div>
                <Button onClick={handleBooking} disabled={isBooking || !selectedDate || !selectedSlot} className="w-full py-4 text-base font-medium disabled:opacity-50 border-0 text-white" style={{ backgroundColor: '#E5B1A1' }} data-testid="appointment-submit">
                  {isBooking ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Calendar size={18} />
                      {tx(language, 'Confirmer le rendez-vous', 'Confirm appointment', 'Confirmar a consulta')}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

      </div>
    </section>
  );
};

// ==================== NEWSLETTER SECTION (NOIR MAT) ====================
const NewsletterSection = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/newsletter`, { email, language });
      setIsSubscribed(true);
      toast.success(tx(language, 'Inscription confirmée', 'Subscription confirmed', 'Inscription confirmée'));
    } catch (error) {
      toast.error(tx(language, 'Une erreur est survenue', 'An error occurred', 'Ocorreu um erro'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 lg:py-24 bg-[#1A1A1B]" data-testid="newsletter-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1]/20 to-transparent" />
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <Mail size={28} className="text-[#E5B1A1] mx-auto mb-6" strokeWidth={1} />
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            {tx(language, "Restez informé des opportunités Cap-Vert", 'Stay informed about Cape Verde opportunities', "Restez informé des opportunités Cap-Vert")}
          </h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            {tx(language, "Recevez en avant-première nos nouvelles opportunités d'investissement et analyses du marché capverdien.", "Receive our new investment opportunities and Cape Verdean market analyses before anyone else.", "Recevez en avant-première nos nouvelles opportunités d'investissement et analyses du marché capverdien.")}
          </p>

          {isSubscribed ? (
            <div className="flex items-center justify-center gap-3 text-[#E5B1A1]">
              <Check size={20} />
              <span className="font-medium">{tx(language, 'Vous êtes inscrit !', "You're subscribed!", 'Vous êtes inscrit !')}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tx(language, 'Votre email', 'Your email', 'Votre email')}
                className="flex-1 py-3 bg-white/10 border-[#E5B1A1]/20 text-white focus:border-[#E5B1A1] placeholder:text-white/40"
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 text-sm font-medium border-0 text-white whitespace-nowrap"
                style={{ backgroundColor: '#E5B1A1' }}
                data-testid="newsletter-submit"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  tx(language, "S'inscrire", 'Subscribe', "Subscrever")
                )}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== MAIN LANDING PAGE ====================
const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header />
      <HeroSection />
      <ExpertiseSection />
      <StatsSection />
      <ServicesVisualSection />
      <TestimonialsSection />
      <PortfolioSection />
      <ContactSection />
      <NewsletterSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
